import { useEffect } from 'react';
import type { OutreachFirstTouchDraft } from '@discovr/types';
import { useFirstTouchDraftList, useSelectFirstTouchDraft } from '@discovr/features';
import { Button } from '../../../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/ui/popover';

function timeOf(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? '' : at.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** Drafts are numbered oldest first, so "Draft 2 of 3" keeps its number when a newer one is written. */
export function draftNumber(drafts: OutreachFirstTouchDraft[], index: number): number {
  return drafts.length - index;
}

export interface DraftPickerProps {
  /** Newest first, as Go lists them. */
  drafts: OutreachFirstTouchDraft[];
  frozen: boolean;
  busy?: boolean;
  onPick: (messageId: number) => void;
}

/** R17-66, option B: "Draft N of M" at a creator card's top right. It opens a short list; the one marked Selected is the
 *  draft that goes out, and picking another makes that one the current draft. A frozen list (a first touch is queued or sent) is read-only. */
export function DraftPicker({ drafts, frozen, busy, onPick }: DraftPickerProps) {
  const currentIndex = drafts.findIndex((draft) => draft.current);
  if (drafts.length === 0 || currentIndex < 0) return null;
  const label = `Draft ${draftNumber(drafts, currentIndex)} of ${drafts.length}`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" size="sm" variant="outline" aria-label={`${label}, choose a draft`}>
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2">
        <ul className="flex flex-col gap-1" aria-label="Drafts for this creator">
          {drafts.map((draft, index) => (
            <li key={draft.message.id}>
              <button
                type="button"
                aria-pressed={draft.current}
                disabled={busy || frozen || draft.current}
                className="flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-2 text-left hover:bg-accent focus-visible:bg-accent focus-visible:outline-hidden disabled:cursor-default disabled:hover:bg-transparent"
                onClick={() => onPick(draft.message.id)}
              >
                <span className="flex w-full items-center gap-2 text-body-sm-medium text-foreground">
                  {`Draft ${draftNumber(drafts, index)}`}
                  {index === 0 ? <span className="text-caption-regular text-muted-foreground">Latest</span> : null}
                  {draft.current ? <span className="ml-auto text-caption-regular text-(--brand-600)">Selected</span> : null}
                </span>
                <span className="text-caption-regular text-muted-foreground">
                  {[timeOf(draft.message.createdAt), draft.edited ? 'Edited by you' : draft.writtenBy === 'you' ? 'Written by you' : 'Written by the assistant'].filter(Boolean).join(' · ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

export interface DraftMenuProps {
  entryId: string;
  /** The draft the card shows now: when it changes (a new draft was written) the list is read again. */
  currentId: number;
  campaignId: string;
  canWrite: boolean;
  /** The message id of the draft the admin made current: the wizard shows it, not the newer one. */
  onPicked: (messageId: number) => void;
}

/** The connected menu: reads the creator's drafts (never writing one) and picks one on a click. */
export function DraftMenu({ entryId, currentId, campaignId, canWrite, onPicked }: DraftMenuProps) {
  const list = useFirstTouchDraftList(entryId);
  const select = useSelectFirstTouchDraft(campaignId);
  const { refetch } = list;
  useEffect(() => {
    void refetch();
  }, [currentId, refetch]);
  if (!list.data) return null;
  return (
    <DraftPicker
      drafts={list.data.drafts}
      frozen={list.data.frozen || !canWrite}
      busy={select.isPending}
      onPick={(messageId) => select.mutate({ entryId, messageId }, { onSuccess: () => onPicked(messageId) })}
    />
  );
}
